/**
 * Service Worker Registration
 * 
 * This module handles the registration of the service worker for cache management
 * and provides utilities for cache invalidation.
 */

// Check if service workers are supported
const isServiceWorkerSupported = 'serviceWorker' in navigator;

/**
 * Register the service worker for cache management
 * @returns Promise that resolves when registration is complete
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isServiceWorkerSupported) {
    console.warn('Service workers are not supported in this browser');
    return null;
  }
  
  try {
    // Use current timestamp to ensure we always get a fresh service worker
    const buildTimestamp = new Date().getTime().toString();
    
    // Force unregister any existing service worker first
    await unregisterServiceWorkers();
    
    // Then register the new service worker with cache-busting query parameter
    const registration = await navigator.serviceWorker.register(
      `/service-worker.js?v=${buildTimestamp}`,
      { scope: '/' }
    );
    
    console.log('Service worker registered successfully:', registration);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('Service worker is active');
    
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
};

/**
 * Invalidate the cache to force fresh content
 * @returns Promise that resolves when invalidation is complete
 */
export const invalidateCache = async (): Promise<boolean> => {
  if (!isServiceWorkerSupported || !navigator.serviceWorker.controller) {
    return false;
  }
  
  try {
    // Send message to service worker to invalidate cache
    navigator.serviceWorker.controller.postMessage({
      type: 'INVALIDATE_CACHE',
      timestamp: new Date().toISOString()
    });
    
    // Wait for response from service worker
    return new Promise((resolve) => {
      const messageHandler = (event: MessageEvent) => {
        if (event.data && event.data.type === 'CACHE_INVALIDATED') {
          navigator.serviceWorker.removeEventListener('message', messageHandler);
          resolve(true);
        }
      };
      
      navigator.serviceWorker.addEventListener('message', messageHandler);
      
      // Set timeout in case service worker doesn't respond
      setTimeout(() => {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
        resolve(false);
      }, 3000);
    });
  } catch (error) {
    console.error('Cache invalidation failed:', error);
    return false;
  }
};

/**
 * Unregister all service workers
 * @returns Promise that resolves when unregistration is complete
 */
export const unregisterServiceWorkers = async (): Promise<boolean> => {
  if (!isServiceWorkerSupported) {
    return false;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(registration => registration.unregister()));
    return true;
  } catch (error) {
    console.error('Service worker unregistration failed:', error);
    return false;
  }
};

export default {
  registerServiceWorker,
  invalidateCache,
  unregisterServiceWorkers,
  isServiceWorkerSupported
};
